import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Loader2,
  FileText,
  Check,
  FileArchive,
  FolderOpen,
  Image,
  File,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type {
  Skill,
  CreateSkillRequest,
  UpdateSkillRequest,
  FileTreeNode,
} from '../../lib/skill-types';
import { SKILL_DESCRIPTION_MAX } from '../../constants/skills.constants';

interface SkillConfigFormProps {
  skill?: Skill | null;
  workspaceId: string | null;
  onBack: () => void;
  onSave: (data: CreateSkillRequest | UpdateSkillRequest) => Promise<void>;
}

const DEFAULT_SKILL_TEMPLATE = `---
name: My Skill
description: Describe what this skill does
version: 1.0.0
category: general
---

# Instructions

Provide detailed instructions for how the AI should use this skill.

# Input Schema

\`\`\`json
{
  "type": "object",
  "properties": {
    "input": {
      "type": "string",
      "description": "Description of the input parameter"
    }
  },
  "required": ["input"]
}
\`\`\`

# Examples

Provide usage examples here.
`;


interface FileEntry {
  name: string;
  path: string;
  size: number;
  isDirectory: boolean;
  depth: number;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(entry: FileEntry) {
  if (entry.isDirectory) return <FolderOpen className="size-3.5 text-primary/70 shrink-0" />;
  const ext = entry.name.split('.').pop()?.toLowerCase();
  if (ext && ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'ico'].includes(ext)) {
    return <Image className="size-3.5 text-purple-500/70 shrink-0" />;
  }
  if (ext && ['md', 'txt', 'json', 'yaml', 'yml'].includes(ext)) {
    return <FileText className="size-3.5 text-orange-500/70 shrink-0" />;
  }
  return <File className="size-3.5 text-muted-foreground shrink-0" />;
}

function flattenFileTree(nodes: FileTreeNode[], depth = 0): FileEntry[] {
  const entries: FileEntry[] = [];
  for (const node of nodes) {
    entries.push({
      name: node.name,
      path: node.path,
      size: node.size ?? 0,
      isDirectory: node.type === 'directory',
      depth,
    });
    if (node.children && node.children.length > 0) {
      entries.push(...flattenFileTree(node.children, depth + 1));
    }
  }
  return entries;
}

export function SkillConfigForm({ skill, workspaceId, onBack, onSave }: SkillConfigFormProps) {
  const [formData, setFormData] = useState({
    skillName: skill?.skillName || '',
    description: skill?.description || '',
    content: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(!!skill);
  const [existingFileTree, setExistingFileTree] = useState<FileEntry[]>([]);
  const [isFileTreeOpen, setIsFileTreeOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;

    if (skill && workspaceId) {
      import('../../lib/skill-api').then(async (api) => {
        try {
          const details = await api.fetchSkillDetails(skill.id, workspaceId);
          if (cancelled) return;
          const nextData = {
            skillName: skill.skillName,
            description: skill.description || '',
            content: details.content,
          };
          setFormData(nextData);
          const tree = Array.isArray(details.fileTree) ? flattenFileTree(details.fileTree) : [];
          setExistingFileTree(tree);
        } catch (err) {
          if (!cancelled) {
            console.error('Failed to load skill content:', err);
            setError('Failed to load skill content');
          }
        } finally {
          if (!cancelled) {
            setLoadingContent(false);
          }
        }
      });
    } else {
      setFormData((prev) => ({ ...prev, content: DEFAULT_SKILL_TEMPLATE }));
      setExistingFileTree([]);
      setLoadingContent(false);
    }

    return () => {
      cancelled = true;
    };
  }, [skill, workspaceId]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);

    if (formData.description && formData.description.length > SKILL_DESCRIPTION_MAX) {
      setError(`Description must be less than ${SKILL_DESCRIPTION_MAX} characters`);
      setSaving(false);
      return;
    }

    try {
      const data = skill
        ? {
            skillName: formData.skillName,
            description: formData.description || undefined,
            content: formData.content,
          }
        : {
            skillName: formData.skillName,
            skillType: 'user' as const,
            description: formData.description || undefined,
            content: formData.content,
          };

      await onSave(data);
      // onSave handler in SkillList already switches view back to list
      // after loadSkills() completes. No need to call onBack() here.
    } catch (err) {
      console.error('Failed to save skill:', err);
      setError(err instanceof Error ? err.message : 'Failed to save skill');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b">
        <div className="flex items-center gap-2.5">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-7 w-7 p-0 shrink-0">
            <ArrowLeft className="size-4" />
          </Button>
          <div className="flex items-center justify-center size-8 rounded-lg bg-primary/10">
            <FileText className="size-4 text-primary" />
          </div>
          <div>
            <h1 className="text-base font-semibold leading-tight">
              {skill ? 'Edit Skill' : 'Create Skill'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {skill ? 'Update skill configuration' : 'Define a new skill for your agents'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-auto px-4 py-4 space-y-4">
        {error && (
          <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-md border border-destructive/20">
            <span>{error}</span>
          </div>
        )}

        {loadingContent ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="size-6 text-primary animate-spin mb-3" />
            <p className="text-sm text-muted-foreground">Loading skill content...</p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="skillName" className="text-xs font-medium">Skill Name</Label>
              <Input
                id="skillName"
                value={formData.skillName}
                onChange={(e) => setFormData({ ...formData, skillName: e.target.value })}
                placeholder="e.g., Data Visualizer"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-medium">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of what this skill does"
              />
              <div className={`text-[11px] ${formData.description.length > SKILL_DESCRIPTION_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
                {formData.description.length}/{SKILL_DESCRIPTION_MAX}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="content" className="text-xs font-medium">SKILL.md Content</Label>
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Skill content in markdown format..."
                className="w-full min-h-[280px] p-3 text-xs font-mono leading-relaxed border rounded-md resize-y bg-card focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring transition-colors"
                required
              />
            </div>

            {existingFileTree.length > 0 && (
              <div className="space-y-2">
                <button
                  type="button"
                  className="flex items-center justify-between w-full px-3 py-2 rounded-md border bg-muted/40 text-xs font-medium"
                  onClick={() => setIsFileTreeOpen(!isFileTreeOpen)}
                >
                  <span className="flex items-center gap-2">
                    <FileArchive className="size-3.5 text-muted-foreground" />
                    Existing Files
                  </span>
                  {isFileTreeOpen ? (
                    <ChevronDown className="size-3.5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="size-3.5 text-muted-foreground" />
                  )}
                </button>

                {isFileTreeOpen && (
                  <div className="rounded-md border bg-card max-h-48 overflow-auto py-1">
                    {existingFileTree.map((entry) => (
                      <div
                        key={entry.path}
                        className="flex items-center gap-1.5 px-3 py-1 text-xs hover:bg-muted/40 transition-colors"
                        style={{ paddingLeft: 12 + entry.depth * 16 }}
                      >
                        {getFileIcon(entry)}
                        <span className={entry.isDirectory ? 'font-medium text-foreground' : 'text-foreground'}>
                          {entry.name}{entry.isDirectory ? '/' : ''}
                        </span>
                        {!entry.isDirectory && entry.size > 0 && (
                          <span className="text-[10px] text-muted-foreground ml-auto tabular-nums shrink-0">
                            {formatSize(entry.size)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  These files are from the existing skill and cannot be edited directly.
                </p>
              </div>
            )}
          </>
        )}
      </form>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t bg-muted/30">
        <Button variant="outline" size="sm" onClick={onBack} disabled={saving || loadingContent}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => handleSubmit()} disabled={saving || loadingContent}>
          {saving ? (
            <>
              <Loader2 className="size-3.5 mr-1.5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="size-3.5 mr-1.5" />
              {skill ? 'Update Skill' : 'Create Skill'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
